import { Component, Inject, ViewEncapsulation } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { ActiveSessionInfo } from 'app/core/auth/auth.service';

export interface SessionConflictDialogData {
    activeSession: ActiveSessionInfo;
}

@Component({
    selector: 'session-conflict-dialog',
    templateUrl: './session-conflict-dialog.component.html',
    encapsulation: ViewEncapsulation.None,
})
export class SessionConflictDialogComponent {

    constructor(
        public dialogRef: MatDialogRef<SessionConflictDialogComponent, boolean>,
        @Inject(MAT_DIALOG_DATA) public data: SessionConflictDialogData,
    ) {}

    get info(): ActiveSessionInfo {
        return this.data.activeSession;
    }

    /**
     * Friendly label for the IP: hides loopback ("This computer") and unknown values,
     * since neither tells the user anything meaningful.
     */
    get prettyIpAddress(): string {
        const ip = (this.info?.ipAddress || '').trim();
        if (!ip) { return 'Unknown'; }
        if (ip === '127.0.0.1' || ip === '::1' || ip === '::ffff:127.0.0.1') {
            return 'This computer (local)';
        }
        return ip;
    }

    /** Best-effort: pull "Chrome 130 on macOS" out of a User-Agent string. */
    get prettyUserAgent(): string {
        const ua = this.info?.userAgent || '';
        if (!ua) { return 'Unknown browser'; }

        const browser = /Edg\//.test(ua) ? 'Edge'
            : /Chrome\//.test(ua) ? 'Chrome'
            : /Firefox\//.test(ua) ? 'Firefox'
            : /Safari\//.test(ua) ? 'Safari'
            : 'Browser';

        const os = /Windows NT/.test(ua) ? 'Windows'
            : /Mac OS X/.test(ua) ? 'macOS'
            : /Android/.test(ua) ? 'Android'
            : /iPhone|iPad/.test(ua) ? 'iOS'
            : /Linux/.test(ua) ? 'Linux'
            : 'Unknown OS';

        return `${browser} on ${os}`;
    }

    /** Human-readable relative time, e.g. "5 minutes ago". */
    get lastSeenRelative(): string {
        if (!this.info?.lastSeenOn) { return ''; }
        const ts = new Date(this.info.lastSeenOn).getTime();
        if (!ts) { return ''; }
        const diffSec = Math.max(0, Math.floor((Date.now() - ts) / 1000));
        if (diffSec < 60) { return `${diffSec} second${diffSec === 1 ? '' : 's'} ago`; }
        const diffMin = Math.floor(diffSec / 60);
        if (diffMin < 60) { return `${diffMin} minute${diffMin === 1 ? '' : 's'} ago`; }
        const diffHr = Math.floor(diffMin / 60);
        if (diffHr < 24) { return `${diffHr} hour${diffHr === 1 ? '' : 's'} ago`; }
        const diffDay = Math.floor(diffHr / 24);
        return `${diffDay} day${diffDay === 1 ? '' : 's'} ago`;
    }

    confirm(): void {
        this.dialogRef.close(true);
    }

    cancel(): void {
        this.dialogRef.close(false);
    }
}
